import { useState, useEffect } from "react";
import {
  Space,
  Box,
  Text,
  Grid,
  Flex,
  Loader,
  Paper,
  Tooltip,
  Stack,
  Button,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import JSZip from "jszip";
import {
  IconAlertTriangle,
  IconCloudUpload,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { Dropzone } from "@mantine/dropzone";

import { File as SharedFile, Order, User } from "../types/interfaces";
import { FileType, UserType } from "../types/enums";
import { isUserStaff } from "../utils/user.utils";
import {
  get3OXZFile,
  getTraceabilitiesFile,
  getImagesFile,
  buildOrderFilesArchive,
  downloadFile,
  fetchBlobWithRetry,
  hasExtractedFiles,
} from "../utils/file.utils";
import { useFiles } from "../utils/useFiles";
import { FileList } from "../stories/FileList/FileList";
import { Title } from "../stories/Title/Title";

// Entries listed in the "Download all files" tooltip before "+N more".
const MAX_LISTED_ARCHIVE_ENTRIES = 12;

interface OrderFilesDisplayProps {
  order: Order;
  user: User;
  fileWithPath: SharedFile[];
  refetch: () => void;
  setPictureToMarkAsRead: (picture: any) => void;
}

const OrderFilesDisplay = ({
  order,
  user,
  fileWithPath,
  refetch,
  setPictureToMarkAsRead,
}: OrderFilesDisplayProps) => {
  const [allFiles, setAllFiles] = useState<SharedFile[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  // A 3OXZ could not be downloaded or unzipped: its scans are missing
  const [hasArchiveLoadFailed, setHasArchiveLoadFailed] =
    useState<boolean>(false);
  const { uploadFileToS3, deleteOneFile } = useFiles(order.id);

  const createFileFromUrl = async (url: string) => {
    const data = await fetchBlobWithRetry(url);
    const metadata = {
      type: "application/x-zip-compressed",
    };
    return new File([data], "case.zip", metadata);
  };

  const otherFiles = getImagesFile(allFiles).filter(
    file =>
      !file.name?.toLowerCase().endsWith(".png") &&
      !file.name?.toLowerCase().endsWith(".dcm") &&
      !file.name?.toLowerCase().endsWith(".stl")
  );

  const getStaffFiles = (files: SharedFile[]): SharedFile[] => {
    return files
      .map(file => ({ ...file, name: file.name ?? file.key }))
      .filter(
        file => file.fileType.toLowerCase() !== FileType.CHAT_FILE.toLowerCase()
      );
  };

  const getDcmFiles = () => {
    return allFiles
      .map(file => ({ ...file, name: file.name ?? file.key }))
      .filter(file => file.name?.toLowerCase().endsWith(".dcm"));
  };

  const getStlFiles = () => {
    return allFiles
      .map(file => ({ ...file, name: file.name ?? file.key }))
      .filter(file => file.name?.toLowerCase().endsWith(".stl"));
  };

  // Impression files only, whoever is logged in (#1796): the DCMs already
  // extracted from the 3OXZ plus the STL uploads. The raw 3OXZ stays out
  // because it also carries PNG previews, the case file and patient data.
  const archiveGroups = {
    DCM: getDcmFiles(),
    STL: getStlFiles(),
  };
  const archivedFilesCount = Object.values(archiveGroups).reduce(
    (count, files) => count + files.length,
    0
  );

  const archiveEntryLabels = Object.entries(archiveGroups).flatMap(
    ([folder, files]) =>
      files.map(file => `${folder}/${(file.name ?? file.key).split("/").pop()}`)
  );
  const archiveTooltip = (
    <Stack gap={2}>
      {archiveEntryLabels.slice(0, MAX_LISTED_ARCHIVE_ENTRIES).map(label => (
        <Text key={label} size="xs">
          {label}
        </Text>
      ))}
      {archiveEntryLabels.length > MAX_LISTED_ARCHIVE_ENTRIES && (
        <Text size="xs" fs="italic">
          +{archiveEntryLabels.length - MAX_LISTED_ARCHIVE_ENTRIES} more
        </Text>
      )}
    </Stack>
  );

  const downloadAllFiles = async () => {
    setIsArchiving(true);
    try {
      const archive = await buildOrderFilesArchive(archiveGroups);
      downloadFile(archive, `${order.orderReference}-impressions.zip`);
    } catch (error) {
      console.error("Error building the order files archive:", error);
      notifications.show({
        title: "Could not download the files",
        message: error instanceof Error ? error.message : "",
        color: "red",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const getDCMAndPNG = async (
    allFiles: SharedFile[]
  ): Promise<{ files: SharedFile[]; hasFailed: boolean }> => {
    const orderFiles = allFiles.filter(
      file =>
        file.fileType.toLowerCase() ===
        FileType.ORDER_FILE.toLowerCase().replace("_", "")
    );
    if (orderFiles.length === 0) {
      return { files: [], hasFailed: false };
    }
    const output: SharedFile[] = [];
    let hasFailed = false;

    await Promise.all(
      orderFiles.map(async element => {
        // Its DCM scans were extracted on the server and are listed as plain
        // files: only staff still download the archive, for the previews
        const hasExtractedScans = hasExtractedFiles(element.id, allFiles);
        try {
          if (element.key.toLowerCase().includes("3oxz")) {
            if (hasExtractedScans && !isUserStaff(user)) {
              return;
            }

            const zip = new JSZip();
            const zipfile = await createFileFromUrl(element.url);
            const unzip = await zip.loadAsync(zipfile);

            await Promise.all(
              Object.keys(unzip.files).map(async (zipname, index) => {
                try {
                  const zipnamesplit = zipname.split(".");
                  const fileExtension =
                    zipnamesplit[zipnamesplit.length - 1].toLowerCase();
                  const fileName = zipname.split("/").pop() || "";

                  if (
                    (fileExtension === "dcm" && !hasExtractedScans) ||
                    (fileExtension === "png" &&
                      fileName.startsWith("AutoGeneratedImages") &&
                      !zipname.includes("PrintableOrderForm/user/"))
                  ) {
                    const file = await Object.values(unzip.files)[index];
                    const blobFile = await file.async("blob");
                    const mimeType =
                      fileExtension === "dcm" ? "octet/stream" : "image/png";
                    const blob = new Blob([blobFile], { type: mimeType });
                    const objectUrl = window.URL.createObjectURL(blob);

                    output.push({
                      url: objectUrl,
                      key: zipname,
                      id: zipname,
                      fileType: FileType.ORDER_FILE,
                      name: fileName,
                    } as SharedFile);
                  }
                } catch (innerError) {
                  console.error(
                    `Error extracting file ${zipname}:`,
                    innerError
                  );
                }
              })
            );
          }
        } catch (error) {
          console.error(`Error processing file ${element.key}:`, error);
          // Only the previews are missing when the scans were extracted
          if (!hasExtractedScans) {
            hasFailed = true;
          }
        }
      })
    );
    return { files: output, hasFailed };
  };

  const loadFiles = async () => {
    setIsFilesLoading(true);
    const staffFiles = await getStaffFiles(fileWithPath);
    const extracted = await getDCMAndPNG(fileWithPath);
    setAllFiles([...staffFiles, ...extracted.files]);
    setHasArchiveLoadFailed(extracted.hasFailed);
    setIsFilesLoading(false);
  };

  useEffect(() => {
    loadFiles();
  }, [JSON.stringify(fileWithPath), fileWithPath.length]);

  return (
    <div id="order-files-display">
      <Space h="xl" />
      <Title size={5} title="Files" mb="xs" />

      {isFilesLoading ? (
        <Flex align="center" justify="center" style={{ padding: "20px" }}>
          <Loader size="sm" />
          <Text ml="sm">Loading files...</Text>
        </Flex>
      ) : (
        <>
          {hasArchiveLoadFailed && (
            <Alert
              color="red"
              icon={<IconAlertTriangle size={20} />}
              title="Scan files could not be loaded"
              mb="md"
            >
              <Text size="sm" mb="sm">
                The download failed or was interrupted. Try again; if it keeps
                failing, contact Jasper in the chat.
              </Text>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconRefresh size={16} />}
                onClick={loadFiles}
              >
                Retry
              </Button>
            </Alert>
          )}

          {isUserStaff(user) && (
            <>
              {/* Main 3OXZ Files Section */}
              {(() => {
                const oxzFiles = get3OXZFile(allFiles);
                return (
                  oxzFiles.length > 0 && (
                    <>
                      <Box mb="md">
                        {oxzFiles.map(file => (
                          <a
                            key={file.id}
                            download={file.key ?? file.name}
                            href={file.url}
                          >
                            <Flex align="center">OrderFile (3OXZ)</Flex>
                          </a>
                        ))}
                      </Box>
                    </>
                  )
                );
              })()}

              {/* Traceability Files Section */}
              {getTraceabilitiesFile(allFiles).length > 0 && (
                <>
                  <Title size={6} title="Traceability Files" mt="md" mb="xs" />
                  <FileList
                    files={getTraceabilitiesFile(allFiles)}
                    deleteOneFile={deleteOneFile}
                    showTitle={false}
                    showDate={true}
                    blueLink={true}
                    refetch={refetch}
                    deleteFilter={[FileType.LABS_FILE]}
                    notificationFilter={[FileType.ORDER_ADDON_FILE]}
                    onclick={setPictureToMarkAsRead}
                  />
                </>
              )}

              {/* PNG Files Section */}
              {allFiles.filter(file =>
                file.name?.toLowerCase().endsWith(".png")
              ).length > 0 && (
                <>
                  <Title
                    size={6}
                    title="Preview Images (PNG)"
                    mt="md"
                    mb="xs"
                  />
                  <Grid>
                    {allFiles
                      .filter(file => file.name?.toLowerCase().endsWith(".png"))
                      .map(file => (
                        <Grid.Col key={file.id} span={6}>
                          <a
                            href={file.url}
                            download={file.name}
                            style={{
                              textDecoration: "none",
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              setPictureToMarkAsRead(file);
                            }}
                          >
                            <Box
                              style={{
                                border: "1px solid #eee",
                                borderRadius: "4px",
                                padding: "8px",
                                marginBottom: "10px",
                                height: "100%",
                              }}
                            >
                              <Flex direction="column" align="center">
                                <Box
                                  style={{
                                    height: "140px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={file.url}
                                    alt={file.name}
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "130px",
                                      objectFit: "contain",
                                    }}
                                  />
                                </Box>
                                <Text
                                  size="xs"
                                  style={{
                                    marginTop: "8px",
                                    textAlign: "center",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {file.name?.replace(
                                    "AutoGeneratedImages.Models.",
                                    ""
                                  )}
                                </Text>
                              </Flex>
                            </Box>
                          </a>
                        </Grid.Col>
                      ))}
                  </Grid>
                </>
              )}
            </>
          )}

          {allFiles.length > 0 && (
            <>
              {/* DCM Files Section */}
              {allFiles.filter(file =>
                file.name?.toLowerCase().endsWith(".dcm")
              ).length > 0 && (
                <>
                  <Title size={6} title="Scan Files (DCM)" mt="md" mb="xs" />
                  <FileList
                    files={getDcmFiles()}
                    deleteOneFile={deleteOneFile}
                    showTitle={false}
                    blueLink={true}
                    refetch={refetch}
                    deleteFilter={[FileType.LABS_FILE]}
                    notificationFilter={[FileType.ORDER_ADDON_FILE]}
                    onclick={setPictureToMarkAsRead}
                  />
                </>
              )}

              {/* STL Files Section */}
              {allFiles.filter(file =>
                file.name?.toLowerCase().endsWith(".stl")
              ).length > 0 && (
                <>
                  <Title size={6} title="3D Models (STL)" mt="md" mb="xs" />
                  <FileList
                    files={getStlFiles()}
                    deleteOneFile={deleteOneFile}
                    showTitle={false}
                    blueLink={true}
                    refetch={refetch}
                    deleteFilter={[FileType.LABS_FILE]}
                    notificationFilter={[FileType.ORDER_ADDON_FILE]}
                    onclick={setPictureToMarkAsRead}
                  />
                </>
              )}

              {/* Other Files through FileList */}
              {otherFiles.length > 0 && (
                <>
                  <Title size={6} title="Other files" mt="md" mb="xs" />
                  <FileList
                    files={otherFiles}
                    deleteOneFile={deleteOneFile}
                    refetch={refetch}
                    deleteFilter={[FileType.LABS_FILE]}
                    showTitle={
                      !allFiles.some(
                        file =>
                          file.name?.toLowerCase().endsWith(".png") ||
                          file.name?.toLowerCase().endsWith(".dcm") ||
                          file.name?.toLowerCase().endsWith(".stl")
                      )
                    }
                    blueLink={true}
                    notificationFilter={[FileType.ORDER_ADDON_FILE]}
                    onclick={setPictureToMarkAsRead}
                    showDate={true}
                  />
                </>
              )}
            </>
          )}

          {/* File Upload Dropzone */}
          {user.type !== UserType.SUPPLIER && (
            <Dropzone
              accept={{
                "image/*": [],
                "application/dicom": [".DCM", ".dcm"],
                "model/stl": [".stl", ".STL"],
              }}
              onDrop={async files => {
                await uploadFileToS3([...files], FileType.LABS_FILE);
                refetch();
              }}
              style={{
                border: "2px dashed gray",
                borderRadius: "5px",
                padding: "10px",
                marginTop: "20px",
                cursor: "pointer",
              }}
            >
              <Flex justify="center" align="center">
                <IconCloudUpload size={20} />
                <Box ml="sm">
                  <Text size="sm" style={{ fontWeight: "bold" }}>
                    Add a file to this order
                  </Text>
                </Box>
              </Flex>
            </Dropzone>
          )}

          {archivedFilesCount > 0 && (
            <Tooltip label={archiveTooltip} openDelay={1000} withArrow>
              <Button
                variant="default"
                fullWidth
                mt="md"
                leftSection={<IconDownload size={20} />}
                loading={isArchiving}
                onClick={downloadAllFiles}
              >
                Download all files ({archivedFilesCount})
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

export default OrderFilesDisplay;
